import PropTypes from "prop-types";
import React, { useState } from "react";
import BuyOnStoreButton from "web/react/components/buy-on-store-button/buy-on-store-button";
import BuyOnStoreButtonWithDiscount from "web/react/components/buy-on-store-button-with-discount/buy-on-store-button-with-discount";
import classnames from "classnames";
import ColorPicker from "web/react/components/color-picker/color-picker";
import HintBubble from "web/react/components/hint-bubble/hint-bubble";
import { languageIsEnglish } from "web/script/modules/language";
import PriceInfo from "web/react/components/price-info/price-info";
import ProductDescriptionDetails from "web/react/components/product-description/product-description-details/product-description-details";
import ProductDescriptionExpressCheckout from "web/react/components/product-description/product-description-express-checkout/product-description-express-checkout";
import ProductDescriptionHeader from "web/react/components/product-description/product-description-header/product-description-header";
import ProductDescriptionPriceHistoryBullet from "web/react/components/product-description/product-description-price-history-bullet/product-description-price-history-bullet";
import ProductDescriptionShipping from "web/react/components/product-description/product-description-shipping/product-description-shipping";
import ProductDescriptionComparePrice from "web/react/components/product-description/product-description-compare-prices/product-description-compare-prices";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
import SizePicker from "web/react/components/size-picker/size-picker";
import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import { BuyOptionPropType } from "web/types/buy-option";
import { DesignerPropType } from "web/types/designer";
import { ProductPropType } from "web/types/product";
import SendToAppModal from "web/react/components/send-to-app-modal/send-to-app-modal";
import {
    RawBranchLink,
    FinancialIncentivesCampaignRawBranchLink,
} from "web/script/utils/branch-io";
import browser from "web/script/utils/browser";
import environment from "web/script/modules/environment";
import history from "web/script/utils/history";
import analytics from "web/script/analytics/analytics";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import Promos from "./product-area-promos/product-area-promos";
import styles from "./product-area.css";

const DEFAULT_BLOCK = "soft_block";
const NO_BLOCK = "no_block";
const APP_MODAL_NAME = "send-to-app-on-is-product-shop-now-modal";

export const ProductAreaPropType = PropTypes.shape({
    all_sizes_sorted: PropTypes.array,
    buy_options: PropTypes.objectOf(BuyOptionPropType).isRequired,
    should_display_sizes: PropTypes.bool,
    products: PropTypes.objectOf(ProductPropType).isRequired,
    designer: DesignerPropType.isRequired,
    absolute_url: PropTypes.string.isRequired,
    is_product_area_accordions_enabled: PropTypes.bool,
    sorted_buy_options: PropTypes.arrayOf(PropTypes.string).isRequired,
    sorted_variants: PropTypes.arrayOf(PropTypes.string).isRequired,
    available_sizes: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    lead_label: PropTypes.string.isRequired,
    discount_label: PropTypes.string,
    sale_count_label: PropTypes.string,
    terms_url: PropTypes.string.isRequired,
    show_express_checkout: PropTypes.bool.isRequired,
    size_guide_link: PropTypes.string,
    express_sizes: PropTypes.arrayOf(
        PropTypes.shape({
            option_value: PropTypes.shape({
                link_id: PropTypes.string,
                raw_size: PropTypes.string,
                icon: PropTypes.bool,
            }),
            human_size: PropTypes.string,
            raw_size: PropTypes.string,
            min_price: PropTypes.string,
        })
    ),
    price_history_bullet: PropTypes.string,
    is_express_checkout_size_test: PropTypes.bool,
    express_checkout_disclaimer: PropTypes.bool,
    bing_traffic: PropTypes.bool,
});

function ProductArea({
    productArea: {
        all_sizes_sorted: allSizesSorted,
        buy_options: buyOptions,
        should_display_sizes: shouldDisplaySizes,
        products,
        designer,
        is_product_area_accordions_enabled: isProductAreaAccordionsEnabled,
        sorted_buy_options: allSortedBuyOptions,
        sorted_variants: sortedVariants,
        lead_label: leadLabel,
        discount_label: discountLabel,
        sale_count_label: saleCountLabel,
        terms_url: termsUrl,
        show_express_checkout: showExpressCheckout,
        size_guide_link: sizeGuideLink,
        express_sizes: expressSizes,
        price_history_bullet: priceHistoryBullet,
        bing_traffic: bingTraffic,
    },
    handleProductChange,
    selectedVariant,
}) {
    if (!selectedVariant) {
        // If we're rendered by the old PDP code, we won't get this prop and need to improvise
        selectedVariant = sortedVariants[0];
    }

    let product = products[selectedVariant];

    let sortedBuyOptions = allSortedBuyOptions.filter((linkId) =>
        product.link_ids.includes(linkId)
    );
    let buyOption = buyOptions[sortedBuyOptions[0]];
    let size = buyOption.sizes[buyOption.default_size];
    let isLoggedIn = React.useMemo(() => userProfiler.isLoggedIn(), []);

    let currentPriceHtml = size.formatted_sale_price;
    let priceWithCurrencySymbol = size.formatted_price;

    let selectedShipping;
    let freeShipping;
    let expressShipping;
    let freeReturn = false;
    if (size.returns) {
        freeReturn = size.returns.is_free;
    }
    if (size.shipping_info) {
        let { cheapest, cheapest_guaranteed: guaranteed, fastest } = size.shipping_info;
        if (cheapest && cheapest.is_free) {
            freeShipping = cheapest;
        }
        if (guaranteed) {
            selectedShipping = guaranteed;
        } else {
            selectedShipping = cheapest;
        }
        expressShipping = fastest;
    }
    let shouldRenderShipping = freeShipping || expressShipping || freeReturn;

    let promotion;
    if (size.promotion) {
        let promo = size.promotion;
        let retailerTerms = promo.terms_url;
        if (!retailerTerms) {
            retailerTerms = size.lead_url;
        }
        promotion = {
            tcs: [
                {
                    // "Lyst promotion terms apply"
                    message: gettext("in_stock_product.lyst_promotion_terms_link"),
                    url: termsUrl,
                },
                {
                    // "{retailer_name} promotion terms apply"
                    message: gettext("in_stock_product.retailer_promotion_terms_link", {
                        retailer_name: buyOption.retailer_name,
                    }),
                    url: retailerTerms,
                },
            ],
            message: promo.html_message,
            url: promo.url,
        };
    }
    // TODO: Move this to product-description-price when we kill the old serializer
    // "Price last checked {interval}"
    let humanLastChecked = gettext("in_stock_product.last_checked_at", {
        interval: buyOption.date_updated_human,
    });

    const isIos = browser.ios;
    let blockVariant = NO_BLOCK;
    // Always get to the best price faster in the app
    let sendToAppTitle = gettext("is_product.shop_now.app_overlay_block.title_1");
    let sendToAppBranchLink = RawBranchLink.InStockShopNow;
    let sendToAppFinancialIncentiveWebviewToken;
    let downloadAppCTA;
    if (isIos && promotion == null && languageIsEnglish() && !environment.get("isPlaUtmCampaign")) {
        blockVariant = DEFAULT_BLOCK;
        if (environment.getFeature("mg_block_affiliate_shop_now_fi")) {
            // Get {reward_value} back as a reward if you buy through the Lyst App
            sendToAppTitle = gettext("financial_incentive.app_download_overlay.title", {
                reward_value: "10%",
            });
            sendToAppBranchLink = FinancialIncentivesCampaignRawBranchLink.InStockModal;
            sendToAppFinancialIncentiveWebviewToken = environment.getFeature(
                "mg_block_affiliate_shop_now_fi"
            );
            // Get {reward_value} back on this item
            downloadAppCTA = gettext("financial_incentive.is_shop_now_app_download_overlay.cta", {
                reward_value: "10%",
            });
        }
    }

    // PMT-212 - Show shipping info in accordian instead of overlay for EC products
    let expressShippingLinkId = null;
    if (showExpressCheckout) {
        isProductAreaAccordionsEnabled = true;
        expressShippingLinkId = sortedBuyOptions[0];
    }

    // MG-194 - Add Hard/Soft block to Download App on Shop Now button
    let retailAreaText;
    if (blockVariant !== NO_BLOCK && !showExpressCheckout) {
        // Best price from over 450 stores
        retailAreaText = gettext("in_stock_product.from_450_stores.label");
    }
    let [blocked, setBlocked] = useState(true);
    const blockModalIsOpen = !!useOverlayWatcher(APP_MODAL_NAME);

    function onBlockModalOpen() {
        if (history.getCurrentState().overlay !== APP_MODAL_NAME) {
            history.pushOverlay(APP_MODAL_NAME);
        }
    }

    function onButtonClick(callbackFunction, event) {
        analytics.event("buy_area", "clicked", "buy-on-store-button");
        event.preventDefault();
        if (isIos && !userProfiler.isLoggedIn()) {
            if (blockVariant === DEFAULT_BLOCK && blocked) {
                setBlocked(false);
                onBlockModalOpen();
                return;
            }
        }
        // If we don't meet the requirements to open the modal, we immediately invoke the callback
        callbackFunction(event);
    }

    function onBlockModalClose() {
        history.popOverlay(APP_MODAL_NAME);
    }

    const saveForLaterButton = (
        <SaveForLaterButton
            isLoggedIn={isLoggedIn}
            type="normal"
            fullWidth={false}
            productId={product.product_id}
            pageSource="pdp"
            // "Track deals"
            cta={showExpressCheckout ? gettext("designer_updates.track_deals_btn_text") : undefined}
            showExpressCheckout={showExpressCheckout}
            bingTraffic={bingTraffic}
        />
    );

    const expressCheckoutBuyOnStoreButton = (
        <BuyOnStoreButton
            productId={product.product_id}
            leadUrl={size.lead_url}
            leadLabel={leadLabel}
            bingTraffic={bingTraffic}
        />
    );

    let productDescriptionExpressCheckoutClassname = styles.expressBuySection;

    const productDescriptionExpressCheckout = (
        <ProductDescriptionExpressCheckout
            className={productDescriptionExpressCheckoutClassname}
            sizeGuideLink={sizeGuideLink}
            sizes={expressSizes}
            leadUrl={size.lead_url}
            leadLabel={buyOption.retailer_name}
            productId={product.product_id}
            bingTraffic={bingTraffic}
            storeLeadUrl={size.lead_url}
        />
    );

    return (
        <div>
            <ProductDescriptionHeader
                className={styles.headerSection}
                designerName={designer.name}
                designerUrl={designer.url}
                isDesignerActive={designer.active}
                shortDescription={product.short_description}
            />
            <div className={styles.section}>
                <div className={styles.price}>
                    <PriceInfo
                        isOnSale={size.is_on_sale}
                        retailerName={buyOption.retailer_name}
                        leadUrl={size.lead_url}
                        saleCountLabel={saleCountLabel}
                        formattedPrice={priceWithCurrencySymbol}
                        selectedShipping={selectedShipping}
                        currentPriceHtml={currentPriceHtml}
                        retailAreaText={retailAreaText}
                    />
                    {sortedBuyOptions.length > 1 && blockVariant === NO_BLOCK && (
                        <ProductDescriptionComparePrice
                            allSizesSorted={allSizesSorted}
                            buyOptions={buyOptions}
                            shouldDisplaySizes={shouldDisplaySizes}
                            sortedBuyOptions={sortedBuyOptions}
                            inlineVariant={true}
                        />
                    )}
                </div>
                <Promos promotion={promotion} />
                {!showExpressCheckout && (
                    <div className={classnames(styles.leadButtons, styles.buttonsInRow)}>
                        {/* Affiliate PDP mobile */}
                        {discountLabel && (
                            <BuyOnStoreButtonWithDiscount
                                productId={product.product_id}
                                leadUrl={size.lead_url}
                                leadLabel={leadLabel}
                                discountLabel={discountLabel}
                                beforeOnClick={onButtonClick}
                            />
                        )}

                        {!discountLabel && (
                            <BuyOnStoreButton
                                productId={product.product_id}
                                leadUrl={size.lead_url}
                                leadLabel={leadLabel}
                                beforeOnClick={onButtonClick}
                            />
                        )}

                        {languageIsEnglish() && (
                            <SaveForLaterButton
                                isLoggedIn={isLoggedIn}
                                type="normal"
                                fullWidth={false}
                                productId={product.product_id}
                                pageSource="pdp"
                                // "Track deals"
                                cta={gettext("designer_updates.track_deals_btn_text")}
                            />
                        )}
                    </div>
                )}
                <div
                    className={classnames(styles.leadButtons, {
                        [styles.buttonsInColumn]: !showExpressCheckout,
                        [styles.expressCheckoutButtonsInRow]: showExpressCheckout,
                    })}
                >
                    {/* Affiliate PDP desktop & Express Checkout PDP */}
                    {!showExpressCheckout && discountLabel && (
                        <BuyOnStoreButtonWithDiscount
                            productId={product.product_id}
                            leadUrl={size.lead_url}
                            leadLabel={leadLabel}
                            discountLabel={discountLabel}
                        />
                    )}
                    {!showExpressCheckout && !discountLabel && (
                        <BuyOnStoreButton
                            productId={product.product_id}
                            leadUrl={size.lead_url}
                            leadLabel={leadLabel}
                        />
                    )}

                    {/* PMT-978: Different button layout options */}
                    {languageIsEnglish() && saveForLaterButton}
                    {showExpressCheckout && expressCheckoutBuyOnStoreButton}
                </div>
                {humanLastChecked && buyOption.is_amazon && (
                    <div className={styles.lastCheckedWrapper}>
                        <HintBubble
                            tooltipText={
                                gettext("in_stock_product.last_checked_at_tooltip") // "Product prices and availability are accurate as of the date/time indicated and are subject to change. Any price and availability information displayed on partners' sites at the time of purchase will apply to the purchase of this product."
                            }
                        />
                        <span title={buyOption.date_updated}>{humanLastChecked}</span>
                    </div>
                )}
                <SendToAppModal
                    title={sendToAppTitle}
                    url={sendToAppBranchLink}
                    isOpen={blockModalIsOpen}
                    onClose={onBlockModalClose}
                    analyticsEventLabel={"is-product-shop-now"}
                    productImageURL={product.images[0].full_size_url}
                    trackedLink={{
                        leadUrl: size.lead_url,
                        productId: product.product_id,
                        reason: "continue-to-retailer",
                    }}
                    financialIncentiveWebviewToken={sendToAppFinancialIncentiveWebviewToken}
                    downloadCTA={downloadAppCTA}
                />
            </div>
            {shouldDisplaySizes && allSizesSorted && (
                <div className={styles.section}>
                    <div className={styles.inlineSubHeader}>
                        <h3 className={styles.title}>
                            {gettext("in_stock_product.sizes.title") // Sizes
                            }
                            {":"}
                        </h3>
                        {sortedBuyOptions.length > 1 && blockVariant === NO_BLOCK && (
                            <ProductDescriptionComparePrice
                                allSizesSorted={allSizesSorted}
                                shouldDisplaySizes={shouldDisplaySizes}
                                inlineVariant={true}
                                buyOptions={buyOptions}
                                sortedBuyOptions={sortedBuyOptions}
                                title={
                                    gettext("in_stock_product.shop_all_sizes.link") // Shop All Sizes
                                }
                                analyticsTitle={"shop_all_sizes"}
                            />
                        )}
                    </div>
                    <SizePicker allSizesSorted={allSizesSorted} buyOptionSizes={buyOption.sizes} />
                </div>
            )}
            {sortedVariants.length > 1 && (
                <div className={styles.section}>
                    <h3 className={styles.title}>
                        {gettext("in_stock_product.colors.title") // Colors
                        }
                        {":"}
                    </h3>
                    <ColorPicker
                        allSortedBuyOptions={allSortedBuyOptions}
                        buyOptions={buyOptions}
                        current={selectedVariant}
                        onClick={handleProductChange}
                        variants={sortedVariants}
                        products={products}
                    />
                </div>
            )}
            {/* PMT-978: Different button layout options */}
            {showExpressCheckout && productDescriptionExpressCheckout}
            {(shouldRenderShipping || showExpressCheckout) && (
                <ProductDescriptionShipping
                    productAreaAccordions={isProductAreaAccordionsEnabled}
                    leadUrl={size.lead_url}
                    retailerName={buyOption.retailer_name}
                    freeShipping={freeShipping}
                    freeReturn={freeReturn}
                    expressShipping={expressShipping}
                    className={styles.section}
                    linkId={expressShippingLinkId}
                />
            )}
            {priceHistoryBullet && (
                <ProductDescriptionPriceHistoryBullet
                    productAreaAccordions={isProductAreaAccordionsEnabled}
                    className={styles.section}
                    priceHistoryBullet={priceHistoryBullet}
                />
            )}
            <ProductDescriptionDetails
                className={classnames(styles.section, {
                    [styles.detailsSectionAccordion]: isProductAreaAccordionsEnabled,
                })}
                product={product}
                productAreaAccordions={isProductAreaAccordionsEnabled}
            />
        </div>
    );
}

ProductArea.propTypes = {
    productArea: ProductAreaPropType.isRequired,
    handleProductChange: PropTypes.func,
    selectedVariant: PropTypes.string,
};

export default ProductArea;
